import type { ReactNode } from 'react';
import { Divider, Header, Popup, Segment } from 'semantic-ui-react';
import type { ReportValueChangeType, FinancialReportChangesType } from '../../../../../schemas/financialReportChanges.schemas';
import '../../updatesTab.css';

function FinancialReportChanges({ title, reportChanges, restricted }: {
    title: string;
    reportChanges?: FinancialReportChangesType | null;
    restricted?: boolean;
}) {
    if (!reportChanges) {
        return (
            <Segment>
                <Header as="h4">{title}</Header>
                Not detected
            </Segment>
        );
    }
    const { schemaDiff, reportsDiff } = reportChanges.changes
    const { addedDescriptors, removedDescriptors } = schemaDiff
    const { addedReports, removedReports, reports } = reportsDiff

    const reportMetadataLabels: Record<string, string> = { year: "Year", period: "Period" }
    const fieldLabels = Object.assign({}, reportMetadataLabels, reportsDiff.descriptors)

    const reportValueRows: ReactNode[][] = []
    if (Object.keys(reportsDiff.descriptors).length > 0) {
        for (const descriptorId of Object.keys(fieldLabels)) {
            const reportValueRow = []
            reportValueRow.push(<td key="descriptor">{fieldLabels[descriptorId]}</td>)
            reportValueRow.push(<td key="descriptor-id">{descriptorId.replace("custom__", "").slice(0, 8)}</td>)
            for (const [reportIndex, report] of reports.entries()) {
                reportValueRow.push(reportMetadataLabels[descriptorId] === undefined ?
                    <ReportValueChangeCell key={reportIndex} change={report.diffs[descriptorId]} restricted={restricted} /> :
                    <td key={reportIndex}>{report[descriptorId as 'year' | 'period']}</td>)
            }

            reportValueRows.push(reportValueRow)
        }
    }

    const statistics = reportChanges.statistic
    return (
        <Segment>
            <Header as="h4">{title}</Header>
            <Header as="h6" className='thinVeritcalMargin'>Summary</Header>
            <table className='tableUpdateDiffTable statistics'>
                <tbody>
                    <tr>
                        <td>Added Reports</td>
                        <td>{statistics.addedReports} </td>
                    </tr>
                    <tr>
                        <td>Removed Reports</td>
                        <td>{statistics.removedReports} </td>
                    </tr>
                    <tr>
                        <td>Added Core Values</td>
                        <td>{statistics.addedCoreValues} </td>
                    </tr>
                    <tr>
                        <td>Changed Core Values</td>
                        <td>{statistics.changedCoreValues} </td>
                    </tr>
                    <tr>
                        <td>Removed Core Values</td>
                        <td>{statistics.removedCoreValues} </td>
                    </tr>
                    <tr>
                        <td>Missing Labels</td>
                        <td>{statistics.addedLabels} </td>
                    </tr>
                    <tr>
                        <td>Changed Labels</td>
                        <td>{statistics.changedLabels} </td>
                    </tr>
                    <tr>
                        <td>Removed Labels</td>
                        <td>{statistics.removedLabels} </td>
                    </tr>
                    <tr>
                        <td>Added cells beacuse of added field to the model</td>
                        <td>{statistics.dataAddedBecauseOfNewDescriptor} </td>
                    </tr>
                    <tr>
                        <td>Removed cells beacuse of removed field from the model</td>
                        <td>{statistics.dataRemovedBecauseOfRemovedDescriptor} </td>
                    </tr>
                </tbody>
            </table>
            <Divider />
            <Header as="h6" className='thinVeritcalMargin' >Cell Changes</Header>
            {reportValueRows.length === 0 ? "Not detected" :
                <table className='tableUpdateDiffTable'>
                    <tbody>
                        {reportValueRows.map((row, rowIndex) => {
                            return <tr key={rowIndex}>
                                {row}
                            </tr>
                        })}
                    </tbody>
                </table>
            }
            <Divider />

            <Header as="h6" className='thinVeritcalMargin'>Model changes</Header>
            <AddedAndRemovedReportFields
                addedFieldLabels={addedDescriptors}
                removedFieldLabels={removedDescriptors} />
            <Divider />

            <Header as="h6" className='thinVeritcalMargin' >Added/Removed reports</Header>
            <AddedAndRemovedReports
                addedReports={addedReports}
                removedReports={removedReports} />

        </Segment>
    )
}

function AddedAndRemovedReports({ addedReports, removedReports }: {
    addedReports: string[];
    removedReports: string[];
}) {
    return (
        <>
            {addedReports.length === 0 && removedReports.length === 0 ? "Not detected" :
                <>
                    <ul>
                        {addedReports.map((r, idx) => <li className='greenText' key={idx} >{r}</li>)}
                        {removedReports.map((r, idx) => <li className='redText' key={"_" + idx} >{r}</li>)}
                    </ul>
                </>}
        </>
    )
}

function AddedAndRemovedReportFields({ addedFieldLabels, removedFieldLabels }: {
    addedFieldLabels: Record<string, string>;
    removedFieldLabels: Record<string, string>;
}) {
    const addedFieldIds = Object.keys(addedFieldLabels)
    const removedFieldIds = Object.keys(removedFieldLabels)
    return (
        <>
            {addedFieldIds.length === 0 && removedFieldIds.length === 0 ? "Not detected" :
                <table className='tableUpdateDiffTable statistics'>
                    <tbody>
                        <tr>
                            <td>Field Name</td>
                            <td>Field Id</td>
                        </tr>
                        {addedFieldIds.map((id, idx) =>
                            <tr className='greenText' key={idx} >
                                <td>{addedFieldLabels[id]}</td>
                                <td>{id.replace("custom__", "").slice(0, 8)}</td>
                            </tr>)}
                        {removedFieldIds.map((id, idx) =>
                            <tr className='redText' key={"_" + idx} >
                                <td>{removedFieldLabels[id]}</td>
                                <td>{id.replace("custom__", "").slice(0, 8)}</td>
                            </tr>)}
                    </tbody>
                </table>
            }
        </>
    )
}

function ReportValueChangeCell({ change, restricted }: {
    change?: ReportValueChangeType;
    restricted?: boolean;
}) {

    const labelChange = change?.label
    const valueChange = change?.coreValue
    const cell = (
        <td className={valueChange?.kind?.includes('CALCULATED') ? 'updates__cell--calculated' : undefined}>
            {change &&
                <table>
                    <tbody>
                        <tr>
                            {labelChange &&
                                <td>
                                    <span className='redText'> {labelChange.oldVal}</span>
                                    <span className='greenText'> {labelChange.newVal}</span>
                                </td>
                            }
                        </tr>
                        <tr>
                            {valueChange &&
                                <td>
                                    {restricted ?
                                        <span> Unavailable</span> :
                                        <>
                                            <span className='redText'> {valueChange.oldVal}</span>
                                            <span className='greenText'> {valueChange.newVal}</span>
                                        </>
                                    }
                                </td>
                            }
                        </tr>
                    </tbody>
                </table>}
        </td>
    )

    return valueChange?.kind ? <Popup size='tiny' wide on='hover' content={valueChange.kind} trigger={cell} /> : cell
}

export default FinancialReportChanges;
